import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ListViewGlobalValues,
  PreferenceGroup,
  PreferenceKey,
  PreferenceKeyImpl,
} from "@services/userPreferences/types";
import {
  MIN_PAGE_SIZE,
  PreferenceCreationType,
  preferencesDefaultContextValue,
  preferencesToLoad,
} from "./defaults";
import { useSimpleReducer } from "@common/hooks";
import { useErrorNotification } from "../Notification";
import {
  createUserPreference,
  getAllUserPreferences,
  updateUserPreference,
} from "@services/userPreferences";

const PREFERENCES_CHANNEL = "User_Preferences_broadcast_channel";
const BC_MESSAGES = {
  RELOAD: "reload",
};
export type GlobalPreferencesContextData = {
  [PreferenceGroup.LIST_VIEW]: {
    [PreferenceKeyImpl.global]: ListViewGlobalValues;
  };
};
export type GlobalPreferencesContextType = GlobalPreferencesContextData & {
  loaded: boolean;
  errored: boolean;
};

export type GlobalPreferencesDispatchContextType = {
  update: <
    D,
    T extends keyof GlobalPreferencesContextData = PreferenceGroup.LIST_VIEW
  >(
    groupName: T,
    key: keyof GlobalPreferencesContextData[T],
    data: Partial<D>
  ) => Promise<void>;
};

export const GlobalPreferencesContext =
  createContext<GlobalPreferencesContextType>(preferencesDefaultContextValue);

export const GlobalPreferencesDispatchContext =
  createContext<GlobalPreferencesDispatchContextType>({
    update: async () => {},
  });

export const GlobalPreferencesProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const dispatchError = useErrorNotification();
  const broadcastRef = useRef(new BroadcastChannel(PREFERENCES_CHANNEL));
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [state, dispatch] = useSimpleReducer<GlobalPreferencesContextData>(
    preferencesDefaultContextValue
  );

  const getStateData = <T extends keyof GlobalPreferencesContextData>(
    groupName: T,
    key: keyof GlobalPreferencesContextData[T]
  ) => state[groupName][key];

  const load = async () => {
    const mustCreate: PreferenceCreationType[] = [];
    try {
      const preferences = await getAllUserPreferences();

      preferencesToLoad.forEach(({ groupName, keys }) => {
        keys.forEach((key) => {
          const data = preferences.find(
            (x) => x.preferenceGroup === groupName && x.key === key
          );

          let value = data?.value as any;

          if (data) {
            if (
              key === "global" &&
              groupName === "list_view" &&
              !value.bulkImportPageSize
            ) {
              value.bulkImportPageSize = MIN_PAGE_SIZE;
            }

            dispatch({
              [groupName]: {
                [key]: value,
              },
            });
          } else {
            mustCreate.push({ groupName, key });
          }
        });
      });
    } catch (e) {
      dispatchError(e);
      setErrored(true);
    } finally {
      setLoaded(true);
    }
    try {
      /**
       * To make it much simpler when editing, just create the preferences that don't exist yet in BE
       */
      mustCreate.forEach(({ groupName, key }) => {
        createUserPreference(groupName, {
          preferenceGroup: groupName,
          key,
          value: getStateData(groupName, key),
        });
      });
    } catch (e) {
      dispatchError(e);
    }
  };
  const update: GlobalPreferencesDispatchContextType["update"] = async (
    groupName,
    key,
    data
  ) => {
    try {
      await updateUserPreference(groupName, key as PreferenceKey, {
        preferenceGroup: groupName,
        key: key as PreferenceKey,
        value: { ...getStateData(groupName, key), ...data },
      });
      broadcastRef.current.postMessage(BC_MESSAGES.RELOAD);
      load();
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    load();
    broadcastRef.current.onmessage = (ev) => {
      if (ev.data === BC_MESSAGES.RELOAD) load();
    };
    return () => {
      broadcastRef.current.close();
    };
  }, []);

  return (
    <GlobalPreferencesContext.Provider value={{ ...state, loaded, errored }}>
      <GlobalPreferencesDispatchContext.Provider value={{ update }}>
        {loaded && children}
      </GlobalPreferencesDispatchContext.Provider>
    </GlobalPreferencesContext.Provider>
  );
};
